/**
 * ServerConnection — Phase 6 thin-client transport layer.
 *
 * Manages auth token storage, REST intent calls, and the WebSocket
 * lifecycle (connect/reconnect/disconnect, snapshot/delta callbacks).
 *
 * All REST calls go to relative /api paths; Vite dev proxy routes them to
 * the game server. WebSocket connects to /ws (distinct from / so the Vite
 * proxy can target it with ws:true without intercepting normal page loads).
 *
 * Reconnection: exponential backoff 1s → 2s → 4s → 8s → cap 30s.
 * Callbacks registered via onSnapshot/onDelta/onDisconnect are called once
 * and replaced each time you register them, so main.js can call them on
 * boot without memory leaks.
 */

// ---------- token persistence ----------

const TOKEN_KEY = 'seatrader_auth_token';

export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY) || null; }
  catch { return null; }
}

export function saveToken(token) {
  try { localStorage.setItem(TOKEN_KEY, token); }
  catch (e) { console.warn('[ServerConnection] Cannot save token:', e); }
}

export function clearToken() {
  try { localStorage.removeItem(TOKEN_KEY); }
  catch {}
}

// ---------- REST helpers ----------

async function _post(path, body, token) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(path, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  });
  const data = await res.json();
  return data;
}

async function _get(path, token) {
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(path, { headers });
  const data = await res.json();
  return data;
}

// ---------- auth API ----------

/**
 * @param {string} username
 * @param {string} password
 * @param {{ companyName?: string, companyColor?: string, startPortId?: string }} [opts]
 *   Company setup chosen on the register form (src/ui/modals/AuthModal.js).
 *   All optional — the server falls back to its own defaults for anything omitted.
 */
export async function apiRegister(username, password, opts = {}) {
  return _post('/api/auth/register', { username, password, ...opts });
}

export async function apiLogin(username, password) {
  return _post('/api/auth/login', { username, password });
}

export async function apiLogout() {
  const token = getToken();
  if (!token) return { success: true };
  const result = await _post('/api/auth/logout', {}, token);
  clearToken();
  return result;
}

export async function apiMe() {
  const token = getToken();
  if (!token) return null;
  try {
    return await _get('/api/auth/me', token);
  } catch {
    return null;
  }
}

/**
 * Trade the single-use `oidc_exchange` code (from the URL after Keycloak's
 * login redirect completes — see main.js's boot sequence) for a real
 * session token. Same response shape as apiLogin()/apiRegister().
 */
export async function apiOidcExchange(code) {
  return _post('/api/auth/oidc/exchange', { code });
}

// ---------- admin status (Phase 9: admin event injection) ----------

// Cached client-side mirror of the current account's `isAdmin` flag,
// resolved from /api/auth/login, /api/auth/register or /api/auth/me.
// Not itself a security boundary (the server re-checks isAdmin on every
// /api/admin/* call) — purely so the UI knows whether to show admin entry
// points at all.
let _isAdmin = false;

/** Set the cached isAdmin flag. Call after login/register/me resolves. */
export function setIsAdmin(value) {
  _isAdmin = !!value;
}

/** Whether the currently authenticated account is an admin (cached flag). */
export function isAdmin() {
  return _isAdmin;
}

// ---------- game intents ----------

/**
 * POST /api/game/<path> with Bearer token.
 * Returns the parsed JSON response (always has { success } field).
 */
export async function intent(path, body = {}) {
  const token = getToken();
  if (!token) return { success: false, error: 'Not authenticated' };
  try {
    return await _post(`/api/game/${path}`, body, token);
  } catch (err) {
    console.error(`[ServerConnection] Intent ${path} failed:`, err);
    return { success: false, error: err.message };
  }
}

// ---------- admin API (Phase 9: real-world event injection) ----------
//
// Operator-only endpoints, outside the /api/game/* intent namespace. The
// server checks isAdmin on every call (403 if not admin) — the client-side
// isAdmin() flag above only controls whether the UI offers these actions.

async function _adminPost(path, body = {}) {
  const token = getToken();
  if (!token) return { success: false, error: 'Not authenticated' };
  try {
    return await _post(`/api/admin/${path}`, body, token);
  } catch (err) {
    console.error(`[ServerConnection] Admin POST ${path} failed:`, err);
    return { success: false, error: err.message };
  }
}

async function _adminGet(path) {
  const token = getToken();
  if (!token) return { success: false, error: 'Not authenticated' };
  try {
    return await _get(`/api/admin/${path}`, token);
  } catch (err) {
    console.error(`[ServerConnection] Admin GET ${path} failed:`, err);
    return { success: false, error: err.message };
  }
}

/**
 * POST /api/admin/events/trigger — instantly fire a catalog event
 * (skipping the weighted random roll and 3-7 day wait).
 * @param {string} eventId
 * @param {number} [durationDaysOverride]
 * @returns {Promise<{success:true, event:object}|{success:false, error:string}>}
 */
export async function triggerCatalogEvent(eventId, durationDaysOverride) {
  const body = { eventId };
  if (durationDaysOverride !== undefined && durationDaysOverride !== null && durationDaysOverride !== '') {
    body.durationDaysOverride = durationDaysOverride;
  }
  return _adminPost('events/trigger', body);
}

/**
 * POST /api/admin/events/custom — fire an ad-hoc, admin-authored event
 * not present in the static WORLD_EVENTS catalog.
 * @param {{name:string, description:string, modifiers:object, durationDays:number}} params
 * @returns {Promise<{success:true, event:object}|{success:false, error:string}>}
 */
export async function triggerCustomEvent({ name, description, modifiers, durationDays }) {
  return _adminPost('events/custom', { name, description, modifiers, durationDays });
}

/**
 * GET /api/admin/events/catalog — the full WORLD_EVENTS catalog, so the
 * client never has to duplicate src/config/events.js.
 * @returns {Promise<{events: Array}|{success:false, error:string}>}
 */
export async function fetchEventCatalog() {
  return _adminGet('events/catalog');
}

/**
 * GET /api/admin/players — every account with a short game summary.
 * @returns {Promise<{players: Array}|{error:string}>}
 */
export async function fetchAdminPlayers() {
  return _adminGet('players');
}

/**
 * POST /api/admin/players/remove — permanently delete an account and its
 * game progress.
 * @param {string} accountId
 * @returns {Promise<{success:true, username:string}|{error:string}>}
 */
export async function removeAdminPlayer(accountId) {
  return _adminPost('players/remove', { accountId });
}

// ---------- WebSocket lifecycle ----------

let _ws = null;
let _deliberateClose = false;
let _reconnectTimer = null;
let _reconnectDelay = 1000;

let _onSnapshotHandler  = null;
let _onDeltaHandler     = null;
let _onDisconnectHandler = null;

export function onSnapshot(handler)    { _onSnapshotHandler  = handler; }
export function onDelta(handler)       { _onDeltaHandler     = handler; }
export function onDisconnect(handler)  { _onDisconnectHandler = handler; }

export function isConnected() {
  return _ws !== null && _ws.readyState === WebSocket.OPEN;
}

/**
 * Open (or re-open) the WebSocket connection.
 * Uses /ws path so Vite proxy can forward WS upgrades separately from / (HTML).
 */
export function connect() {
  if (_ws && (_ws.readyState === WebSocket.OPEN || _ws.readyState === WebSocket.CONNECTING)) {
    return;
  }

  const token = getToken();
  if (!token) {
    console.warn('[ServerConnection] connect() called without a token');
    return;
  }

  _deliberateClose = false;

  // Build the WS URL: ws(s)://same-origin/ws?token=...
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const url = `${proto}//${location.host}/ws?token=${encodeURIComponent(token)}`;

  console.log('[ServerConnection] Connecting to', url.replace(/token=.*/, 'token=***'));
  _ws = new WebSocket(url);

  _ws.addEventListener('open', () => {
    console.log('[ServerConnection] WebSocket connected');
    _reconnectDelay = 1000; // reset backoff on success
  });

  _ws.addEventListener('message', (event) => {
    let msg;
    try { msg = JSON.parse(event.data); }
    catch (e) { console.error('[ServerConnection] Bad WS message:', e); return; }

    if (msg.type === 'snapshot') {
      if (_onSnapshotHandler) _onSnapshotHandler(msg);
    } else if (msg.type === 'world:notReady') {
      // Server is still initialising. Disconnect and retry after a delay.
      console.warn('[ServerConnection] World not ready:', msg.message);
      if (_onDisconnectHandler) _onDisconnectHandler({ code: 0, reason: 'world:notReady' });
      _scheduleReconnect();
    } else {
      if (_onDeltaHandler) _onDeltaHandler(msg);
    }
  });

  _ws.addEventListener('close', (event) => {
    console.log(`[ServerConnection] WebSocket closed: code=${event.code}`);
    _ws = null;
    if (!_deliberateClose) {
      if (_onDisconnectHandler) _onDisconnectHandler({ code: event.code, reason: event.reason });
      // 4000: the server dropped this connection for a newer one (too many
      // open tabs). Reconnecting would just push another tab off in turn.
      // 4003: an admin removed this account — the token is gone too.
      if (event.code !== 4000 && event.code !== 4003) _scheduleReconnect();
    }
  });

  _ws.addEventListener('error', (err) => {
    console.error('[ServerConnection] WebSocket error:', err);
    // close event will fire afterwards, triggering reconnect
  });
}

export function disconnect() {
  _deliberateClose = true;
  if (_reconnectTimer) { clearTimeout(_reconnectTimer); _reconnectTimer = null; }
  if (_ws) { _ws.close(); _ws = null; }
}

function _scheduleReconnect() {
  if (_deliberateClose) return;
  if (_reconnectTimer) return; // already scheduled

  const delay = _reconnectDelay;
  _reconnectDelay = Math.min(_reconnectDelay * 2, 30000);

  console.log(`[ServerConnection] Reconnecting in ${delay}ms…`);
  _reconnectTimer = setTimeout(() => {
    _reconnectTimer = null;
    connect();
  }, delay);
}
